import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  TemplateRef,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { menuBar, MenuBarService } from '@softapps/core';
import {
  CalendarViewType,
  RosterContent,
  RosterService,
  RosterWithDepartmentHeadHostComponent,
} from '@softapps/pms/roster';
import {
  ButtonComponent,
  DsContextMenuComponent,
  DsContextMenuDividerComponent,
  DsContextMenuItemComponent,
  IconComponent,
  SoftSlotDirective,
  translator,
} from '@softline/ui-core';
import { SelectDsComponent, SelectDsOptionComponent } from '@softline/ui-core/select';
import { GarageRosterService } from '../services/garage-roster.service';

@Component({
  selector: 'soft-garage-roster-menu-bar',
  templateUrl: './menu-bar.component.html',
  styles: `
    :host {
      display: contents;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    SelectDsComponent,
    SelectDsOptionComponent,
    DsContextMenuComponent,
    DsContextMenuItemComponent,
    DsContextMenuDividerComponent,
  ],
})
export class GarageRosterMenuBarComponent {
  readonly service = inject(RosterService);
  readonly menuBarService = inject(MenuBarService);
  readonly hostComponent = inject(RosterWithDepartmentHeadHostComponent);

  /** Platform-correct modifier for keyboard-shortcut chips: ⌘ on macOS/iOS,
      Ctrl elsewhere. */
  readonly modifierKey = /mac|iphone|ipad|ipod/i.test(
    navigator.userAgent || navigator.platform || ''
  )
    ? '⌘'
    : 'Ctrl';

  readonly selectedEvents = computed(() => {
    return this.service.selectedEvents();
  });

  readonly viewTypeOptions = computed<{ v: CalendarViewType; l: string }[]>(() => [
    { v: 'day', l: 'Tag' },
    { v: 'workWeek', l: 'Arbeitswoche' },
    { v: 'week', l: 'Woche' },
    { v: 'month', l: 'Monat' },
    { v: 'planMonth', l: 'Planmonat' },
  ]);

  readonly viewTypeLabel = computed(
    () =>
      this.viewTypeOptions().find((o) => o.v === this.hostComponent.calendarViewType())
        ?.l ?? ''
  );

  readonly isAppointment = computed(() => {
    return (
      this.selectedEvents().length >= 1 &&
      this.selectedEvents().some((o: RosterContent) => {
        const details = o?.additionalData?.['additionaldetail'];
        if (!details || details.length < 1) return false;
        return details[0].tabname === 'ORDER';
      })
    );
  });

  readonly isOrderSelection = computed(() => {
    const events = this.selectedEvents();
    return (
      events.length >= 1 &&
      events.every((o: RosterContent) => {
        const tabname = o?.additionalData?.['additionaldetail']?.[0]?.tabname;
        return tabname === 'ORDER' || tabname === 'OBJECT';
      })
    );
  });

  readonly isTaskSelection = computed(() => {
    const events = this.selectedEvents();
    return (
      events.length >= 1 &&
      events.every((o: RosterContent) =>
        o?.additionalData?.['additionaldetail']?.some((d) => d.tabname === 'GARAGE_TASK')
      )
    );
  });

  readonly viewLeadingMenuBarTemplate = viewChild.required('viewLeadingMenuBar', {
    read: TemplateRef,
  });

  readonly selectionLeadingMenuBarTemplate = viewChild.required(
    'selectionLeadingMenuBar',
    {
      read: TemplateRef,
    }
  );
  readonly selectionTrailingMenuBarTemplate = viewChild.required(
    'selectionTrailingMenuBar',
    {
      read: TemplateRef,
    }
  );

  readonly garageEventMenuTemplate = viewChild.required('garageEventMenu', {
    read: TemplateRef,
  });

  readonly t = translator();

  constructor() {
    this.hostComponent.calendarDetails.set('all');

    effect(() => {
      (this.service as GarageRosterService).garageEventMenu.set(
        this.garageEventMenuTemplate()
      );
    });

    menuBar({
      key: 'pms/roster/view',
      title: this.t('Ansicht'),
      template: this.viewLeadingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      align: 'leading',
      order: 2,
    });

    menuBar({
      key: 'garage/roster/selection',
      title: this.t('Auswahl'),
      template: this.selectionLeadingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      visible: computed(() => this.selectedEvents().length > 0),
      order: 3,
    });

    menuBar({
      key: 'garage/roster/selection',
      title: this.t('Auswahl'),
      template: this.selectionTrailingMenuBarTemplate,
      separated: true,
      containerStyle: 'plain',
      visible: computed(() => this.selectedEvents().length > 0),
      align: 'trailing',
      order: 3,
    });

    effect(() => {
      const selectedEvents = this.selectedEvents();
      if (selectedEvents.length > 0) {
        untracked(() => {
          this.menuBarService.activateKey('garage/roster/selection');
        });
      } else if (selectedEvents.length === 0) {
        untracked(() => {
          this.menuBarService.activateIndex(0);
        });
      }
    });
  }
}
